import React, { useState, useEffect } from "react";
import logo from '../utils/logo.png';

import {
    FaUsers,
    FaWarehouse,
    FaChartLine,
    FaFileInvoice,
    FaFileSignature
} from "react-icons/fa";
import { FiLogOut } from "react-icons/fi";
import { IoStatsChartSharp } from "react-icons/io5";

import CustomersTab from "./tabs/CustomersTab";
import GarageSitesTab from "./tabs/GarageSitesTab";
import MonthlyActivityTab from "./tabs/MonthlyActivityTab";
import BillingInvoiceTab from "./tabs/BillingInvoiceTab";
import ApprovedInvoicesTab from "./tabs/ApprovedInvoicesTab";
import HardwareInvoiceTab from "./tabs/HardwareInvoiceTab";
import QuotationTab from "./tabs/QuotationTab";


import axios from "axios";

/* CHART.JS */
import { Bar, Line } from "react-chartjs-2";
import { SiOpensourcehardware } from "react-icons/si";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    Title
} from "chart.js";
import { BsFileEarmarkCheckFill } from "react-icons/bs";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    Title
);

export default function FacilityMasterHome() {
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const userRole = sessionStorage.getItem("role");

    const allTabs = [
        { id: "dashboard", label: "Dashboard", icon: <IoStatsChartSharp /> },
        { id: "customers", label: "Customers", icon: <FaUsers /> },
        { id: "garages", label: "Garage Sites", icon: <FaWarehouse /> },
        { id: "monthly", label: "Monthly Activity", icon: <FaChartLine /> },
        { id: "billing", label: "Billing Invoice", icon: <FaFileInvoice /> },
        { id: "approved_invoices", label: "Invoices Comparision", icon: <BsFileEarmarkCheckFill /> },
        { id: "quotations", label: "Quotations", icon: <FaFileSignature /> },
        { id: "hardware_invoices", label: "Hardware Invoice", icon: <SiOpensourcehardware /> }
    ];

    const tabs = allTabs.filter(tab => {
        if (userRole === "Admin") return true;

        if (userRole === "Sales") {
            return ["dashboard", "customers", "billing", "approved_invoices"].includes(tab.id);
        }

        if (userRole === "Accounts") {
            return ["dashboard"].includes(tab.id);
        }

        return false;
    });

    const [activeTab, setActiveTab] = useState("dashboard");
    const [customers, setCustomers] = useState([]);
    const [garages, setGarages] = useState([]);
    const [invoiceRevenue, setInvoiceRevenue] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState("ALL");

    useEffect(() => {
        axios.get(`${BASE_URL}/customers/`)
            .then(res => setCustomers(res.data))
            .catch(() => setCustomers([]));

        axios.get(`${BASE_URL}/garages/`)
            .then(res => setGarages(res.data))
            .catch(() => setGarages([]));

        axios.get(`${BASE_URL}/invoice-revenue-summary`)
            .then(res => setInvoiceRevenue(res.data))
            .catch(() => setInvoiceRevenue([]));
    }, []);

    // Customer name map for dropdown
    const customerNameMap = {};
    invoiceRevenue.forEach(r => {
        customerNameMap[r.customer_id] = r.customer_name || `Customer ${r.customer_id}`;
    });

    // Dynamic months sorted
    const months = Array.from(new Set(invoiceRevenue.map(r => r.month))).sort(
        (a, b) => new Date(a) - new Date(b)
    );

    // Aggregate monthly revenue
    const monthlyRevenue = months.map(month => {
        if (selectedCustomerId === "ALL") {
            // sum all customers for this month
            return invoiceRevenue
                .filter(r => r.month === month)
                .reduce((sum, r) => sum + Number(r.total_revenue), 0);
        } else {
            const record = invoiceRevenue.find(
                r => r.customer_id.toString() === selectedCustomerId && r.month === month
            );
            return record ? Number(record.total_revenue) : 0;
        }
    });

    const barChartData = {
        labels: months,
        datasets: [
            {
                label: "Revenue ($)",
                data: monthlyRevenue,
                backgroundColor: "#3b82f6",
                borderRadius: 6
            }
        ]
    };

    const lineChartData = {
        labels: months,
        datasets: [
            {
                label: "Revenue Trend ($)",
                data: monthlyRevenue,
                borderColor: "#f97316",
                backgroundColor: "rgba(249,115,22,0.2)",
                tension: 0.4,
                fill: true,
                pointRadius: 5
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `$${context.raw.toLocaleString()}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: v => `$${v}`
                }
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 text-gray-700">
            {/* HEADER */}
            <div className="bg-white shadow-sm px-6 py-4 border-b">
                <div className="flex items-center justify-between">

                    {/* LEFT SIDE */}
                    <div className="flex items-center gap-3">
                        <img src={logo} alt="Logo" className="h-12" />
                        <div>
                            <h1 className="text-lg font-semibold">
                                ParkEngage Facility Master
                            </h1>
                            <p className="text-xs text-gray-500">
                                Digital at Every Touchpoint™
                            </p>
                        </div>
                    </div>

                    {/* RIGHT SIDE */}
                    <button
                        onClick={() => {
                            const confirmLogout = window.confirm("Are you sure you want to logout?");
                            if (confirmLogout) {
                                sessionStorage.clear();
                                window.location.href = "/";
                            }
                        }}
                        className="flex items-center gap-2 
                 border border-red-500 text-red-500 
                 px-4 py-2 rounded-lg 
                 hover:bg-red-500 hover:text-white 
                 transition-all duration-200 
                 focus:outline-none focus:ring-2 focus:ring-red-300"
                    >
                        <FiLogOut className="text-lg" />
                        Logout
                    </button>

                </div>
            </div>

            {/* TABS */}
            <div className="bg-white shadow-sm px-6">
                <div className="flex space-x-8">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-3 flex items-center space-x-2 border-b-2 ${activeTab === tab.id
                                ? "border-blue-600 text-blue-600 font-semibold"
                                : "border-transparent text-gray-600"
                                }`}
                        >
                            {tab.icon}
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="p-6">
                {activeTab === "dashboard" && (
                    <>
                        {/* KPI CARDS
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                            <div className="bg-white p-6 rounded-xl shadow">
                                <p>Total Customers</p>
                                <p className="text-4xl font-bold">{customers.length}</p>
                            </div>

                            <div className="bg-white p-6 rounded-xl shadow">
                                <p>Garage Sites</p>
                                <p className="text-4xl font-bold">{garages.length}</p>
                            </div>
                        </div> */}


                        {/* KPI CARDS */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">

                            {/* TOTAL CUSTOMERS */}
                            <div className="bg-white shadow-lg rounded-xl p-6 flex justify-between items-center border border-gray-100">
                                <div>
                                    <p className="text-gray-500">Total Customers</p>
                                    <p className="text-4xl font-bold">{customers.length}</p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Active customer accounts
                                    </p>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-full shadow-sm">
                                    <FaUsers className="text-blue-500 text-3xl" />
                                </div>
                            </div>

                            {/* GARAGE SITES */}
                            <div className="bg-white shadow-lg rounded-xl p-6 flex justify-between items-center border border-gray-100">
                                <div>
                                    <p className="text-gray-500">Garage Sites</p>
                                    <p className="text-4xl font-bold">{garages.length}</p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        {garages.filter(g => g.Status === "Active").length} active, {garages.filter(g => g.Status === "Maintenance").length} maintenance, {garages.filter(g => g.Status === "Inactive").length} inactive
                                    </p>
                                </div>

                                <div className="bg-orange-50 p-4 rounded-full shadow-sm">
                                    <FaWarehouse className="text-orange-500 text-3xl" />
                                </div>
                            </div>
                        </div>


                        {/* DROPDOWN + BAR CHART */}
                        <div className="bg-white shadow-lg rounded-xl p-6 mb-6">
                            <div className="flex justify-between mb-4">
                                <h3 className="text-xl font-semibold">Monthly Revenue by Customer</h3>
                                <select
                                    className="border rounded px-3 py-2"
                                    value={selectedCustomerId}
                                    onChange={e => setSelectedCustomerId(e.target.value)}
                                >
                                    <option value="ALL">All Customers</option>
                                    {Object.keys(customerNameMap).map(cid => (
                                        <option key={cid} value={cid}>
                                            {customerNameMap[cid]}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="h-72">
                                <Bar data={barChartData} options={chartOptions} />
                            </div>
                        </div>

                        {/* LINE CHART */}
                        <div className="bg-white shadow-lg rounded-xl p-6">
                            <h3 className="text-xl font-semibold mb-4">Revenue Trend</h3>
                            <div className="h-72">
                                <Line data={lineChartData} options={chartOptions} />
                            </div>
                        </div>
                    </>
                )}

                {activeTab === "customers" && <CustomersTab />}
                {activeTab === "garages" && <GarageSitesTab />}
                {activeTab === "monthly" && <MonthlyActivityTab />}
                {activeTab === "billing" && <BillingInvoiceTab />}
                {activeTab === "approved_invoices" && <ApprovedInvoicesTab />}
                {activeTab === "quotations" && <QuotationTab />}
                {activeTab === "hardware_invoices" && <HardwareInvoiceTab />}
            </div>
        </div>
    );
}
